<template>
  <div>
    <template v-if="!!conceptsValidation && !loading">
      <md-card>
        <md-card-content>
          <sinister-info :road-sinister="roadSinister" />
        </md-card-content>
      </md-card>
      <md-card>
        <md-card-content>
          <template v-if="hasQuantification && processIs('VaCo')">
            <div class="md-layout md-alignment-space-between">
              <h3>Conceptos</h3>
              <catalog-menu
                v-if="!!conceptsValidation"
                @input="setCurrentCatalog"
                :value="conceptsValidation.catalog"
                :button-class="
                  conceptsValidation.id && !conceptsValidation.catalog
                    ? 'md-warning'
                    : 'md-success'
                "
                :button-disabled="!!conceptsValidation.id"
                class="ml-auto"
              >
                <template slot="button" slot-scope="{ catalog }">
                  <h6 class="text-left">
                    Utilizando:
                    <strong>{{ catalog.name }}</strong>
                  </h6>
                </template>
                <template slot="no-selected-catalog">No hay catálogo asignado</template>
              </catalog-menu>
            </div>

            <template>
              <concept-selector
                v-if="roadSinister.campId && !disableAll"
                :camp-id="roadSinister.campId"
                :catalog-id="conceptsValidation.catalog"
                :existant-concepts="existantConcepts"
                @conceptAdded="addConcept"
              />
              <concepts-table
                :selection-blacklist="selectionBlacklist"
                :concepts="concepts"
                :selectable="false"
                :quantification="quantification"
                :disableAll="disableAll"
                @removeConcept="removeConcept"
              />
            </template>
          </template>
          <loading-content
            v-else
            error
            error-label="No se pueden agregar conceptos por validar."
            error-description="El siniestro no esta en el proceso de Validacion de Conceptos."
          />
        </md-card-content>

        <md-card-actions md-alignment="space-between">
          <md-button
            :to="{
              name: 'Ficha de Siniestro Carretero',
              params: { id: `${id}` },
            }"
            class="md-round md-simple"
          >
            <md-icon>back</md-icon>
            <strong>Volver a ficha</strong>
          </md-button>

          <md-button
            v-if="hasQuantification && !disableAll"
            class="md-primary md-round"
            :disabled="
              registeringConceptsValidation ||
              concepts.some((c) => !c.supplies.length)
            "
            @click="submitConceptsValidation"
          >
            <span>{{ equalTotals ? "Terminar validación" : "Guardar conceptos" }}</span>
            <md-tooltip v-if="equalTotals" md-direction="bottom">
              El siniestro avanzará a Levantamiento y no se podrán modificar los
              conceptos
            </md-tooltip>
          </md-button>
        </md-card-actions>
      </md-card>
    </template>
    <div v-else>
      <loading-content
        :loading="loading"
        loading-label="Cargando validacion de conceptos..."
        loading-icon="list"
      ></loading-content>
    </div>
  </div>
</template>

<script>
import FField from "./../FField";
import Concept from "./../Concept";
import ConceptsTable from "./ValidationConceptsTable";
import SinisterInfo from "./SinisterInfo";
import ConceptSelector from "./../Quantification/ConceptSelector";
import CatalogMenu from "@/pages/Catalogs/RoadConcepts/CatalogMenu";

import usesAccountInfoMixin from "@/mixins/usesAccountInfo";
import sinisterService from "@/services/sinister";
import purchaseService from "@/services/purchase";
import setsPageTitleMixin from "@/mixins/setsPageTitle";
import processCalculationsMixin from "@/pages/RoadSinisters/File/mixins/processCalculations";
import mapPermissions from "@/plugins/accountPermissions/mapPermissions";

const WORKFORCE_FAMILY_CODE = "MAN";

export default {
  name: "ConceptsValidation",
  props: {
    id: {
      type: [String, Number],
      required: true,
    },
  },
  components: {
    ConceptsTable,
    FField,
    Concept,
    SinisterInfo,
    CatalogMenu,
    ConceptSelector,
  },
  mixins: [setsPageTitleMixin, usesAccountInfoMixin, processCalculationsMixin],
  data() {
    return {
      loading: true,
      roadSinister: { affected: {} },
      quantification: null,

      registeringConceptsValidation: false,
      newConcept: null,
      conceptsValidation: {
        catalog: null,
        concepts: [],
        deletedConcepts: [],
        requirements: {},
      },
      selectionBlacklist: [WORKFORCE_FAMILY_CODE],
    };
  },

  computed: {
    ...mapPermissions("Quantification", "quantificationconcept", ["add"]),
    affectedProcess() {
      const {
        affected: { process },
      } = this.roadSinister;
      return process ? process.abbreviation : "sss";
    },
    affectedId() {
      let { afectado: casualtyId } = this.$route.query;
      const { affected } = this.roadSinister;
      return casualtyId || (affected && affected.id) || null;
    },
    hasQuantification() {
      return !!this.quantification && !!this.quantification.id;
    },
    disableAll() {
      return !this.processIs("VaCo");
    },
    pageTitle() {
      const { sinister } = this.roadSinister;
      const folioJB = sinister ? sinister.folioJB : "";
      return `${this.title} ${folioJB || ""}`;
    },

    title() {
      const conceptsValidation = this.conceptsValidation;
      const qId = conceptsValidation ? conceptsValidation.id : null;
      return `${!qId ? "Crear" : "Ver"} validacion de conceptos`;
    },

    concepts() {
      const conceptsValidation = this.conceptsValidation;
      return conceptsValidation ? conceptsValidation.concepts : [];
    },
    toDoConcepts() {
      return this.concepts.filter((concept) => !concept.notToDo);
    },
    existantConcepts() {
      return this.conceptsValidation.concepts.map(({ concept: id }) => ({
        id,
      }));
    },
    sinisterQuantificationAmount() {
      const { totalAmount } = this.roadSinister.affected;
      return totalAmount;
    },
    totalQuantificationCalculated() {
      const total = this.concepts.reduce((sum, concept) => {
        const price = concept.unitPriceMXN * concept.quantity;
        return +sum + +price.toFixed(2);
      }, 0);
      return +total.toFixed(2);
    },
    equalTotals() {
      return (
        this.sinisterQuantificationAmount == this.totalQuantificationCalculated
      );
    },
  },
  methods: {
    addConcept(concept) {
      const newConcept = {
        ...concept,
        showSupplies: false,
        quantity: 1,
        concept: concept.id,
        id: 0,
      };
      this.concepts.push(newConcept);
    },

    setCurrentCatalog(newCatalog) {
      const { conceptsValidation } = this;
      const { concepts, catalog } = conceptsValidation;
      if (concepts.length && newCatalog && catalog != newCatalog) {
        this.$fire({
          icon: "warning",
          title: "Cambio de catálogo",
          text: "Al cambiar el catálogo se removeran los conceptos de la tabla actual. ¿Continuar?",
          showCancelButton: true,
          cancelButtonText: "Cancelar",
        }).then((result) => {
          if (result.value) {
            conceptsValidation.deletedConcepts = [
              ...conceptsValidation.deletedConcepts,
              ...conceptsValidation.concepts,
            ];
            conceptsValidation.concepts = [];
            conceptsValidation.catalog = newCatalog;
          }
        });
      } else {
        conceptsValidation.catalog = newCatalog;
      }
    },

    removeConcept(concept) {
      const index = this.concepts.indexOf(concept);
      if (concept.id > 0) {
        this.conceptsValidation.deletedConcepts.push(concept);
      }
      this.concepts.splice(index, 1);
    },
    submitConceptsValidation() {
      if (this.hasEmptyConcept) {
        this.$alertMessage({
          text: `No se puede enviar la cuantificación, se agregaron conceptos sin insumos`,
        });
        return;
      }

      // Fetch last sinister info to validate amounts
      this.$loadingMessage();
      sinisterService
        .fetch(this.id)
        .then((sinister) => {
          this.roadSinister = sinister;
          const data = {
            ...this.conceptsValidation,
            catalog: this.conceptsValidation.catalog,
            affected: this.affectedId,
            id: this.quantification.id,
          };
          if (this.equalTotals) {
            let p1 = () =>
              !!data.deletedConcepts.length &&
              purchaseService.updateQuantification(this.affectedId, {
                ...data,
                deletedConcepts: [...data.deletedConcepts],
                concepts: [],
              });

            return Promise.all([
              p1(),
              purchaseService.sendQuantification(this.affectedId, data),
            ])
              .then(() => {
                return purchaseService
                  .sendQuantification(sinister.affected.id, {
                    ...data,
                    concepts: [],
                  })
                  .then(() => {
                    this.$successMessage({ title: `Conceptos registrados` });
                    this.$router.push({
                      name: "Ficha de Siniestro Carretero",
                      params: { id: this.id },
                    });
                  });
              })
              .catch(this.$fireErrorResponse);
          } else {
            this.$confirmMessage(
              {
                title: "Montos distintos",
                confirmButtonText: "Guardar cambios y validar después",
                html: `<p>Los montos son distintos<p/>
              <p>Alta de siniestro: <strong> $${this.sinisterQuantificationAmount}</strong><p/>
              <p>Total calculado: <strong>$${this.totalQuantificationCalculated}</strong></p>
                `,
              },
              () => {
                return purchaseService
                  .updateQuantification(this.affectedId, data)
                  .then(() => {
                    this.$successMessage({ title: `Conceptos registrados` });
                    this.$router.push({
                      name: "Ficha de Siniestro Carretero",
                      params: { id: this.id },
                    });
                  })
                  .catch(this.$fireErrorResponse);
              }
            );
          }
        })
        .catch(this.$fireErrorResponse);
    },
  },
  created() {
    let { afectado: casualtyId } = this.$route.query;
    Promise.all([
      sinisterService.fetch(this.id),
      purchaseService.fetchQuantification(casualtyId),
    ])
      .then(([sinister, quantification]) => {
        this.roadSinister = sinister;

        if (sinister.affected.id === +casualtyId) {
          this.quantification = quantification;
          this.conceptsValidation.concepts = quantification.concepts;
          this.conceptsValidation.catalog = quantification.catalog;
        }
      })
      .finally(() => (this.loading = false));
  },
};
</script>



// WEBPACK FOOTER //
// src/pages/RoadSinisters/File/ConceptsValidation/index.vue